import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import flags from "country-flag-icons/react/3x2";
import { useLang } from "../hooks/useLanguage";

const buildCountries = (locale) => {
  const names = new Intl.DisplayNames([locale], { type: "region" });
  return getCountries()
    .map((cc) => ({ cc, name: names.of(cc) || cc, code: getCountryCallingCode(cc) }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
};

const PhoneField = ({ value, onChange }) => {
  const { lang } = useLang();
  const countries = useMemo(() => buildCountries(lang === "ar" ? "ar" : "en"), [lang]);
  const [country, setCountry] = useState("AE");
  const [national, setNational] = useState("");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);

  const selected = countries.find((c) => c.cc === country) || { cc: "AE", code: "971", name: "UAE" };
  const Flag = flags[country];

  useEffect(() => {
    if (value === "" && national !== "") setNational("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const emit = (cc, nat) => {
    const digits = nat.replace(/[^\d]/g, "").replace(/^0+/, "");
    onChange(digits ? `+${getCountryCallingCode(cc)}${digits}` : "");
  };

  const choose = (cc) => {
    setCountry(cc);
    emit(cc, national);
    setOpen(false);
    setQuery("");
    setHighlight(0);
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? countries.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          `+${c.code}`.includes(q) ||
          c.cc.toLowerCase().includes(q)
      )
    : countries;

  const onListKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlight]) choose(filtered[highlight].cc);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelectorAll('[role="option"]')[highlight];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  return (
    <div ref={rootRef} className="relative flex items-end gap-3" dir="ltr">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={lang === "ar" ? "اختر رمز الدولة" : "Choose country code"}
        className="flex items-center gap-2 border-b border-ivory/20 pb-[0.75rem] shrink-0 hover:border-champagne transition-colors duration-300"
        data-testid="phone-country-button"
      >
        <span className="w-5 h-[15px] overflow-hidden shrink-0 inline-flex">
          {Flag && <Flag className="w-full h-full block" />}
        </span>
        <span className="latin-text text-[0.95rem] text-ivory">+{selected.code}</span>
        <ChevronDown
          size={14}
          className={`text-champagne transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <input
        id="enq-phone"
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        className="field-input"
        value={national}
        onChange={(e) => {
          setNational(e.target.value);
          emit(country, e.target.value);
        }}
        placeholder={lang === "ar" ? "5X XXX XXXX" : "58 560 8119"}
        data-testid="enquiry-phone"
      />
      {open && (
        <div
          className="absolute z-[80] top-full mt-2 start-0 w-[min(320px,80vw)] bg-[#202020] border hairline-dark shadow-2xl"
          data-testid="phone-country-dropdown"
          data-lenis-prevent
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b hairline-dark">
            <Search size={13} className="text-champagne shrink-0" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlight(0);
              }}
              onKeyDown={onListKey}
              className="bg-transparent w-full text-sm text-ivory outline-none placeholder:text-ivory/30"
              placeholder={lang === "ar" ? "ابحث عن دولة أو رمز" : "Search country or code"}
              aria-label={lang === "ar" ? "ابحث عن دولة أو رمز" : "Search country or code"}
              data-testid="phone-country-search"
            />
          </div>
          <ul
            ref={listRef}
            role="listbox"
            aria-label={lang === "ar" ? "قائمة الدول" : "Country list"}
            aria-activedescendant={filtered[highlight] ? `phone-country-option-${filtered[highlight].cc}` : undefined}
            className="lc-country-list max-h-64 overflow-y-auto"
            onKeyDown={onListKey}
            tabIndex={-1}
          >
            {filtered.map((c, i) => {
              const F = flags[c.cc];
              const active = c.cc === country;
              return (
                <li
                  key={c.cc}
                  id={`phone-country-option-${c.cc}`}
                  role="option"
                  aria-selected={active}
                >
                  <button
                    type="button"
                    onClick={() => choose(c.cc)}
                    onMouseEnter={() => setHighlight(i)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-start text-sm transition-colors duration-150 ${
                      i === highlight ? "bg-[#141414]" : ""
                    } ${active ? "text-champagne" : "text-ivory/80"}`}
                    data-testid={`phone-country-option-${c.cc.toLowerCase()}`}
                  >
                    <span className="w-5 h-[15px] overflow-hidden shrink-0 inline-flex">
                      {F && <F className="w-full h-full block" />}
                    </span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="latin-text text-ivory/50 text-xs">+{c.code}</span>
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li className="px-4 py-3 text-xs text-ivory/40">
                {lang === "ar" ? "لا توجد نتائج" : "No matches"}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PhoneField;
